import { useCallback, useEffect, useMemo, useRef } from 'react'
import { useAuth } from './useAuth'
import { useGetAnsattByIdQuery } from './useAnsatte'
import { useSettings } from './useSettings'

export interface Settings {
  selectedRegionId?: string
  selectedAvdelingId?: string
  selectedSeksjonId?: string
  selectedTeamId?: string
}

interface UseAvdeling {
  get: Settings | undefined
  set: (settings: Settings) => void
  isPending: boolean
  isError: boolean
}

export const useDefaultSettings = (
  isEnabled: boolean
): Settings | undefined => {
  const { data: ansatt, isPending: isPendingAnsatt } = useGetAnsattByIdQuery(
    isEnabled ? 'self' : undefined
  )

  return useMemo(() => {
    if (isPendingAnsatt || !isEnabled) {
      return undefined
    }
    return {
      selectedRegionId: ansatt?.regionId,
      selectedAvdelingId: ansatt?.avdelingId,
      selectedSeksjonId: ansatt?.seksjonId
    }
  }, [ansatt, isEnabled, isPendingAnsatt])
}

export const useAvdeling = (): UseAvdeling => {
  const { userId } = useAuth()
  const defaultSettingsHasBeenSet = useRef(false)
  const userSettings = useSettings()
  const settingAvdeling = userSettings.get.avdeling

  const defaultSettings = useDefaultSettings(settingAvdeling == null)
  const setSettings = useCallback(
    (settings: Settings): void => {
      if (userId != undefined) {
        userSettings.set({
          avdeling: settings
        })
      }
    },
    [userId, userSettings]
  )
  useEffect(() => {
    if (
      userSettings.isSuccess &&
      settingAvdeling == null &&
      defaultSettings != null &&
      !defaultSettingsHasBeenSet.current
    ) {
      setSettings(defaultSettings)
      defaultSettingsHasBeenSet.current = true
    }
  }, [defaultSettings, settingAvdeling, setSettings, userSettings.isSuccess])
  return {
    get: settingAvdeling,
    set: setSettings,
    isPending: userSettings.isPending,
    isError: userSettings.isError
  }
}
